import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import { Box, Button, ButtonGroup, ListItemIcon, Menu, MenuItem, Typography, } from '@mui/material';
import { ArrowDropDown, CloudDownload, Description, GetApp, Publish, Replay, } from '@mui/icons-material';
import { StaffButtonsBlockTS } from './staff-buttons-block-ts';
import { request_ztc_config_file } from 'console/redux/ztc-config-download.actions';
import { openErrorDialog } from 'console/dialogs/error/redux/actions';
import { useRefAnchor } from 'shared/hooks/utils';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { useConfirmDialog } from 'shared/hooks/ui/confirm-dialog';
import { close_z3k_restart_dialog, request_z3k_config_file_from_server, send_z3k_command, upload_z3k_config_from_file, } from 'z3k-settings/redux/actions';
import { selectDevice } from 'entities/device/lib';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { str2Config } from 'z3k-parser/parser/config-convertor/parser';
import { FilterFileNewAndOldConfig } from 'z3k-settings/components/service/components/compare-file-config/filter-file-config';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { useComparisonConfig } from 'shared/hooks/device/z3k.compare';
import { useNavigateRoot } from 'shared/hooks/router';
import CircularWithValueLabel from 'z3k-settings/components/material-utils/circular-progress-with-label';
var ConfigAction;
(function (ConfigAction) {
    ConfigAction["download_from_server"] = "download_from_server";
    ConfigAction["download_from_device"] = "download_from_device";
    ConfigAction["upload_from_file"] = "upload_from_file";
    ConfigAction["test"] = "test";
})(ConfigAction || (ConfigAction = {}));
export function ServiceButtons(props) {
    const { device_id, online } = props;
    const [opened, close, open] = useBooleanState(false);
    // const device = useContext(Z3KSettings);
    const device = usePartialAppSelector(selectDevice, device_id);
    const firmware = device?.firmware_version ?? [];
    const [resultCompare, count, compare] = useComparisonConfig(device?.z3k_config);
    const download_state = device != null
        ? useAppSelector((state) => state.ztc_config_download.by_device_id[device.id])
        : null;
    const loading_config = !!download_state?.loading;
    const restart_dialog_open = device != null
        ? !!useAppSelector((state) => state.z3k_settings.restartDialogOpen)
        : false;
    const onDownloadConfig = useAction(request_ztc_config_file);
    const onUploadSettings = useAction(upload_z3k_config_from_file);
    const onDownloadConfigFromServer = useAction(request_z3k_config_file_from_server);
    const onRestartDevice = useAction((device_id) => send_z3k_command(device_id, null, 'RestartDevice', null, null, false));
    const onRestartDialogClose = useAction(close_z3k_restart_dialog);
    const onOpenErrorDialog = useAction(openErrorDialog);
    const [anchorEl, anchorRef, onOpenConfigMenu, onCloseConfigMenu] = useRefAnchor();
    const downloadConfig = useCallback(() => {
        if (device_id != null) {
            onDownloadConfig(device_id);
        }
    }, [onDownloadConfig, device_id]);
    const downloadConfigFromServer = useCallback(() => {
        if (device_id != null) {
            onDownloadConfigFromServer(device_id);
        }
    }, [onDownloadConfigFromServer, device_id]);
    const uploadConfig = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = false;
        input.accept = 'text/plain';
        document.body.appendChild(input);
        input.addEventListener('change', () => {
            try {
                if (input.files == null || device_id == null) {
                    return;
                }
                const file = input.files[0];
                const reader = new FileReader();
                reader.readAsText(file, 'windows-1251');
                reader.onload = () => {
                    if (typeof reader.result === 'string' && device?.z3k_config) {
                        compare(str2Config(reader.result));
                        open();
                    }
                    else {
                        onUploadSettings(device_id, file);
                    }
                };
            }
            catch (error) {
                console.error(error);
            }
            finally {
                document.body.removeChild(input);
            }
        });
        input.click();
    }, [onOpenErrorDialog, onUploadSettings, device_id, compare]);
    const onClickMenuItem = useCallback((key) => (event) => {
        event.preventDefault();
        switch (key) {
            case ConfigAction.download_from_server:
                return downloadConfigFromServer();
            case ConfigAction.download_from_device:
                return downloadConfig();
            case ConfigAction.upload_from_file:
                return uploadConfig();
        }
    }, [downloadConfig, downloadConfigFromServer]);
    const [ConfirmDialog, openConfirmDialog] = useConfirmDialog();
    const restartDevice = useCallback(() => {
        if (device_id != null) {
            onRestartDevice(device_id);
        }
    }, [onRestartDevice, device_id]);
    const closeDialog = useNavigateRoot();
    const onCloseRestartDialog = useCallback(() => {
        if (device_id != null) {
            onRestartDialogClose(device_id);
            closeDialog();
        }
    }, [device_id, onRestartDialogClose]);
    const config_write_status = device?.config_write_status ?? false;
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 2, children: [firmware != null && firmware[0] >= 62 && (_jsx(Button, { variant: "outlined", color: "secondary", startIcon: _jsx(Replay, {}), onClick: openConfirmDialog, size: "small", disabled: config_write_status, children: "\u041F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C" })), _jsxs(ButtonGroup, { variant: "outlined", color: "inherit", onClick: onOpenConfigMenu, size: "small", children: [_jsx(Button, { startIcon: _jsx(Description, {}), children: loading_config ? 'Загрузка...' : 'Конфигурация' }), _jsx(Button, { size: "small", ref: anchorRef, sx: { padding: '2.325px 6px', minWidth: 30 }, children: _jsx(ArrowDropDown, {}) })] }), _jsx(StaffButtonsBlockTS, { device_id: device_id })] }), _jsxs(Menu, { open: !!anchorEl, anchorEl: anchorEl, onClose: onCloseConfigMenu, keepMounted: true, transformOrigin: { vertical: 'top', horizontal: 'left' }, slotProps: { paper: { sx: { maxHeight: 48 * 4.5 } } }, children: [_jsxs(MenuItem, { onClick: onClickMenuItem(ConfigAction.download_from_server), children: [_jsx(ListItemIcon, { sx: {
                                    minWidth: 30,
                                }, children: _jsx(CloudDownload, {}) }), "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u0441 \u0441\u0435\u0440\u0432\u0435\u0440\u0430"] }, ConfigAction.download_from_server), _jsxs(MenuItem, { disabled: !online || loading_config, onClick: onClickMenuItem(ConfigAction.download_from_device), children: [_jsx(ListItemIcon, { sx: {
                                    minWidth: 30,
                                }, children: _jsx(GetApp, {}) }), "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u0441 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430"] }, ConfigAction.download_from_device), _jsxs(MenuItem, { onClick: onClickMenuItem(ConfigAction.upload_from_file), children: [_jsx(ListItemIcon, { sx: {
                                    minWidth: 30,
                                }, children: _jsx(Publish, {}) }), _jsx(Typography, { variant: "inherit", noWrap: true, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0438\u0437\u00A0\u0444\u0430\u0439\u043B\u0430 \u0438\u00A0\u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0432\u00A0\u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E" })] }, ConfigAction.upload_from_file), _jsxs(MessageBox, { open: restart_dialog_open, onClose: onCloseRestartDialog, title: "\u041A\u043E\u043C\u0430\u043D\u0434\u0430 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0430", isInfo: true, children: [_jsx(Typography, { variant: "subtitle1", color: "textSecondary", children: "\u0423\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E \u043F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u0442\u0441\u044F" }), _jsx(CircularWithValueLabel, { close: onCloseRestartDialog, flagTheEnd: device?.online, titleForError: 'Проверьте подключение прибора.' })] })] }), _jsx(ConfirmDialog, { onConfirm: restartDevice, children: "\u0412\u044B \u0443\u0432\u0435\u0440\u0435\u043D\u044B, \u0447\u0442\u043E \u0445\u043E\u0442\u0438\u0442\u0435 \u043F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E?" }), device?.z3k_config && (_jsx(FilterFileNewAndOldConfig, { resultCompare: resultCompare, oldData: device?.z3k_config, device_id: device_id, opened: opened, count: count, close: close }))] }));
}
